import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';

import { BaseDraggableWidget } from '../Card';

import useAsync from '../../../hooks/useAsync';
import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';

import { getMyTasks } from '../../../api/tasks';
import SettingsContext from '../../../context/SettingsContext';

import { classNames } from '../../helpers/functions';
import { CheckIcon } from '@heroicons/react/20/solid';

const entityTypeColors = {
	ticket: 'border-l-4 border-blue-200',
	work_order: 'border-l-4 border-purple-200',
};

const MyTasks = ({ title, isPreview, ...rest }) => {
	const { setOpen: openTicketOverview } = useModal('ticketOverview');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');

	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const hasSettings = settings?.user?.widgetSettings
		? settings?.user?.widgetSettings[rest?.ctx?.data?.id]?.taskFilter
		: null;

	const [selectedFilter, setSelectedFilter] = useState(hasSettings || 'all');

	const {
		value: tasks,
		execute: refreshTasks,
		error: hasError,
		status: isLoadingTasks,
	} = useAsync(getMyTasks, {
		defaultPayload: { completed: false },
	});

	useEffect(() => {
		refreshTasks({ completed: selectedFilter === 'completed' });
	}, [selectedFilter]);

	const handleUpdateSettings = (key, value) => {
		const widgetSettings = settings?.user?.widgetSettings;

		let updatedValue = {};

		if (widgetSettings) {
			if (widgetSettings[rest?.ctx?.data?.id]) {
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: {
						...widgetSettings[rest?.ctx?.data?.id],
						[key]: value,
					},
				};
			} else {
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: { [key]: value },
				};
			}
		} else {
			updatedValue = {
				[rest?.ctx?.data?.id]: { [key]: value },
			};
		}
		updateSettings(
			{
				type: 'user',
				key: 'widgetSettings',
				value: updatedValue,
			},
			{ showNotification: false },
		);
	};

	const handleTaskClick = (task) => {
		if (task.entityType === 'ticket') {
			openTicketOverview({ id: task.entityId });
		} else if (task.entityType === 'work_order') {
			openWorkOrderOverview({ id: task.entityId });
		}
	};

	if (isPreview) {
		return (
			<BaseDraggableWidget
				title="My Tasks"
				category="Tasks"
				description="View tasks assigned to you"
				{...rest}
				isPreview={true}
			/>
		);
	}

	const filteredTasks =
		selectedFilter === 'all'
			? tasks
			: tasks?.filter((t) => t.entityType === selectedFilter);

	const isEmpty =
		filteredTasks?.length === 0
			? { isEmpty: true, flexCenter: true, skipIcon: true }
			: {};
	const isError = !!hasError ? { isError: true, flexCenter: true } : {};

	return (
		<BaseDraggableWidget
			title="My Tasks"
			widgetProps={{
				isLoading: isLoadingTasks === 'pending',
				...isError,
				...isEmpty,
				isEmptyMessage: 'No tasks assigned to you',
				options: [
					{ label: 'All', value: 'all' },
					{ label: 'Tickets', value: 'ticket' },
					{ label: 'Work Orders', value: 'work_order' },
					{ label: 'Completed', value: 'completed' },
				],
				optionSelected: selectedFilter,
				optionsOnChange: (index, value, raw) => {
					setSelectedFilter(raw?.value);
					handleUpdateSettings('taskFilter', raw?.value);
				},
			}}
			{...rest}
		>
			<div>
				{filteredTasks?.length
					? filteredTasks?.map((task) => {
							return (
								<div
									key={task.id}
									className={classNames(
										'py-2 cursor-pointer border-l-4 pl-6',
										entityTypeColors[task?.entityType] || '',
									)}
									onClick={() => handleTaskClick(task)}
								>
									<div className="flex items-start gap-2">
										<div
											className={classNames(
												'flex-shrink-0 h-4 w-4 rounded border-2 flex items-center justify-center mt-0.5',
												task.completed
													? 'bg-indigo-600 border-indigo-600'
													: 'border-gray-300',
											)}
										>
											{task.completed && (
												<CheckIcon className="h-2.5 w-2.5 text-white" />
											)}
										</div>
										<div className="flex-1 min-w-0">
											<span
												className={classNames(
													'font-normal',
													task.completed
														? 'text-gray-400 line-through'
														: 'text-gray-900',
												)}
											>
												{task.content}
											</span>
										</div>
									</div>

									<div className="text-gray-400 text-xxs mt-1 flex items-center gap-2">
										<span
											className={classNames(
												'inline-flex items-center px-1.5 py-0.5 rounded text-xxs font-medium',
												task.entityType === 'ticket'
													? 'bg-blue-100 text-blue-700'
													: 'bg-purple-100 text-purple-700',
											)}
										>
											{task.entityType === 'ticket' ? 'Ticket' : 'Work Order'}
										</span>
										{task.entity && (
											<span className="truncate">
												{task.entityType === 'ticket'
													? task.entity.subject
													: task.entity.title}
											</span>
										)}
									</div>
								</div>
							);
						})
					: []}
			</div>
		</BaseDraggableWidget>
	);
};

export default MyTasks;
